import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Reveal } from './components/Reveal';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { 
  Package, 
  Truck, 
  Wrench, 
  Warehouse, 
  Cable,
  Award,
  Gift,
  Printer,
  FileText,
  MapPin,
  Mail,
  Menu,
  X
} from 'lucide-react';
import ShippingPage from './ShippingPage';
import './App.css';

function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    interestedIn: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields', {
        description: 'First Name, Last Name, Email, and Message are required.'
      });
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      const data = await response.json();
      
      toast.success('Thank you for contacting us!', {
        description: 'Your message has been sent to info@2ticorp.com. We\'ll get back to you as soon as possible.'
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        interestedIn: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again later or email us directly at info@2ticorp.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const services = [
    {
      icon: <Truck className="w-10 h-10 text-accent" />,
      title: 'Shipping',
      description: 'Small Freight and LTL',
      points: ['Reliable delivery', 'Nationwide coverage', 'Real-time tracking']
    },
    {
      icon: <Wrench className="w-10 h-10 text-accent" />,
      title: 'Smart Hands Field Services',
      description: 'On-site technical support',
      points: ['Expert technicians', '24/7 availability', 'Fast response times']
    },
    {
      icon: <Warehouse className="w-10 h-10 text-accent" />,
      title: 'Warehousing',
      description: 'Secure storage solutions',
      points: ['Climate controlled', 'Inventory management', 'Flexible terms']
    },
    {
      icon: <Cable className="w-10 h-10 text-accent" />,
      title: 'Network Cabling Installation',
      description: 'Professional infrastructure',
      points: ['Certified installers', 'Quality materials', 'Full documentation']
    }
  ];

  const products = [
    {
      image: 'https://images.unsplash.com/photo-1718670013921-2f144aba173a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Small Format Printing',
      icon: <FileText className="w-6 h-6" />,
      items: [
        'Business Cards',
        'Flyers & Brochures',
        'Catalogs & Booklets',
        'Marketing Materials',
        'Postcards & Mailers',
        'Letterheads & Envelopes'
      ]
    },
    {
      image: 'https://images.unsplash.com/photo-1693031630369-bd429a57f115?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Large Format Graphics',
      icon: <Printer className="w-6 h-6" />,
      items: [
        'Banners & Signs',
        'Vehicle Wraps',
        'Trade Show Displays',
        'Window Graphics',
        'Wall Murals',
        'Outdoor Signage'
      ]
    },
    {
      image: 'https://images.unsplash.com/photo-1764874299025-d8b2251f307d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Awards & Recognition',
      icon: <Award className="w-6 h-6" />,
      items: [
        'Crystal Awards',
        'Trophies & Cups',
        'Plaques & Plates',
        'Medals & Ribbons',
        'Corporate Awards',
        'Custom Engraving'
      ]
    },
    {
      image: 'https://images.unsplash.com/photo-1625552187662-ac3fac11fcf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Promotional Products',
      icon: <Gift className="w-6 h-6" />,
      items: [
        'Branded Apparel',
        'Corporate Gifts',
        'Trade Show Giveaways',
        'Custom Merchandise',
        'Promotional Items',
        'Employee Recognition'
      ]
    }
  ];

  const clients = [
    { 
      name: 'Johnson & Johnson', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#CC0000" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Arial">J&J</text></svg>`
    },
    { 
      name: 'Kawasaki', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#00A94F" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="12" font-weight="bold" font-family="Arial">KAWASAKI</text></svg>`
    },
    { 
      name: 'Taco Bell', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><defs><linearGradient id="tb" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#662D91"/><stop offset="100%" style="stop-color:#A349A4"/></linearGradient></defs><rect width="120" height="40" fill="url(#tb)" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">TACO BELL</text></svg>`
    },
    { 
      name: 'Marriott', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#8B0E1A" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="12" font-weight="bold" font-family="Georgia">MARRIOTT</text></svg>`
    },
    { 
      name: 'Hyatt', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#1A1A1A" rx="4"/><text x="60" y="25" text-anchor="middle" fill="#C5A572" font-size="14" font-weight="bold" font-family="Georgia">HYATT</text></svg>`
    },
    { 
      name: 'Hilton', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#0057A0" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="14" font-weight="bold" font-family="Arial">HILTON</text></svg>`
    },
    { 
      name: 'Balboa Capital', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#003B5C" rx="4"/><text x="60" y="20" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="Arial">BALBOA</text><text x="60" y="30" text-anchor="middle" fill="#FFD700" font-size="8" font-weight="bold" font-family="Arial">CAPITAL</text></svg>`
    },
    { 
      name: 'SunWest Mortgage', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><defs><linearGradient id="sw" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" style="stop-color:#FF6B35"/><stop offset="100%" style="stop-color:#FFA500"/></linearGradient></defs><rect width="120" height="40" fill="url(#sw)" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">SUNWEST</text></svg>`
    },
    { 
      name: 'Endologix', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#0066B3" rx="4"/><circle cx="20" cy="20" r="8" fill="white"/><text x="65" y="25" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">ENDOLOGIX</text></svg>`
    },
    { 
      name: 'Avanti', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#E31937" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="14" font-weight="bold" font-family="Arial">AVANTI</text></svg>`
    },
    { 
      name: 'McCarthy', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#00529B" rx="4"/><rect x="10" y="10" width="20" height="20" fill="#FFD700"/><text x="70" y="25" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">McCARTHY</text></svg>`
    },
    { 
      name: 'Vortex', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#333333" rx="4"/><circle cx="25" cy="20" r="12" stroke="#00A651" stroke-width="2" fill="none"/><text x="70" y="25" text-anchor="middle" fill="white" font-size="13" font-weight="bold" font-family="Arial">VORTEX</text></svg>`
    },
    { 
      name: 'City of Irvine', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#003DA5" rx="4"/><circle cx="20" cy="20" r="6" fill="#FFD700"/><text x="65" y="17" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">CITY OF</text><text x="65" y="27" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">IRVINE</text></svg>`
    },
    { 
      name: 'City of Santa Ana', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#8B1F24" rx="4"/><polygon points="20,15 25,25 15,25" fill="#FFD700"/><text x="65" y="17" text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="Arial">CITY OF</text><text x="65" y="27" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="Arial">SANTA ANA</text></svg>`
    },
    { 
      name: 'County of Orange', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#FF6B35" rx="4"/><circle cx="20" cy="20" r="8" fill="#FFD700"/><text x="65" y="17" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">COUNTY OF</text><text x="65" y="27" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">ORANGE</text></svg>`
    },
    { 
      name: 'Baccarat', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#1A1A1A" rx="4"/><text x="60" y="25" text-anchor="middle" fill="#FFD700" font-size="12" font-weight="bold" font-family="Georgia" style="font-style:italic">BACCARAT</text></svg>`
    },
    { 
      name: 'Advanced Management Company', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#004B87" rx="4"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="18" font-weight="bold" font-family="Arial">AMC</text></svg>`
    },
    { 
      name: 'Megamex Foods', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><defs><linearGradient id="mm" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" style="stop-color:#C8102E"/><stop offset="50%" style="stop-color:#FFFFFF"/><stop offset="100%" style="stop-color:#006341"/></linearGradient></defs><rect width="120" height="40" fill="url(#mm)" rx="4"/><text x="60" y="20" text-anchor="middle" fill="#1A1A1A" font-size="9" font-weight="bold" font-family="Arial">MEGAMEX</text><text x="60" y="30" text-anchor="middle" fill="#1A1A1A" font-size="9" font-weight="bold" font-family="Arial">FOODS</text></svg>`
    },
    { 
      name: 'Fast Frame', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#E31E24" rx="4"/><rect x="15" y="12" width="16" height="16" stroke="white" stroke-width="2" fill="none"/><text x="70" y="25" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="Arial">FAST FRAME</text></svg>`
    },
    { 
      name: 'King Seafood', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#003B5C" rx="4"/><path d="M 20 25 Q 25 15, 30 25" stroke="#FFD700" stroke-width="2" fill="none"/><text x="70" y="20" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="Arial">KING</text><text x="70" y="30" text-anchor="middle" fill="#FFD700" font-size="9" font-weight="bold" font-family="Arial">SEAFOOD</text></svg>`
    },
    { 
      name: 'ABOTA', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#1C3A70" rx="4"/><polygon points="20,12 25,22 15,22" fill="#B8860B"/><text x="60" y="25" text-anchor="middle" fill="white" font-size="14" font-weight="bold" font-family="Georgia">ABOTA</text></svg>`
    },
    { 
      name: 'Priority Workforce', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#0066B3" rx="4"/><circle cx="18" cy="20" r="3" fill="#FFD700"/><circle cx="25" cy="20" r="3" fill="#FFD700"/><circle cx="32" cy="20" r="3" fill="#FFD700"/><text x="73" y="20" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">PRIORITY</text><text x="73" y="29" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">WORKFORCE</text></svg>`
    },
    { 
      name: 'JW Staffing', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#2C5F8D" rx="4"/><rect x="12" y="12" width="16" height="16" fill="#FFD700"/><text x="75" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold" font-family="Arial">JW</text><text x="75" y="31" text-anchor="middle" fill="white" font-size="9" font-weight="bold" font-family="Arial">STAFFING</text></svg>`
    },
    { 
      name: 'Freedom Equity Group', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#003B5C" rx="4"/><circle cx="22" cy="20" r="8" stroke="#FFD700" stroke-width="2" fill="none"/><path d="M 22 16 L 22 24 M 18 20 L 26 20" stroke="#FFD700" stroke-width="2"/><text x="70" y="17" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">FREEDOM</text><text x="70" y="25" text-anchor="middle" fill="#FFD700" font-size="8" font-weight="bold" font-family="Arial">EQUITY</text><text x="70" y="32" text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="Arial">GROUP</text></svg>`
    },
    { 
      name: 'Scouting America', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#CE1126" rx="4"/><polygon points="20,15 15,25 25,25" fill="#FFD700"/><polygon points="20,15 23,20 17,20" fill="white"/><text x="70" y="17" text-anchor="middle" fill="white" font-size="9" font-weight="bold" font-family="Arial">SCOUTING</text><text x="70" y="28" text-anchor="middle" fill="white" font-size="9" font-weight="bold" font-family="Arial">AMERICA</text></svg>`
    },
    { 
      name: 'SEW', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><defs><linearGradient id="sew" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#008B8B"/><stop offset="100%" style="stop-color:#20B2AA"/></linearGradient></defs><rect width="120" height="40" fill="url(#sew)" rx="4"/><rect x="15" y="15" width="8" height="10" fill="white" opacity="0.3"/><rect x="25" y="15" width="8" height="10" fill="white" opacity="0.5"/><rect x="35" y="15" width="8" height="10" fill="white" opacity="0.7"/><text x="75" y="25" text-anchor="middle" fill="white" font-size="22" font-weight="bold" font-family="Arial">SEW</text></svg>`
    },
    { 
      name: 'Habitat For Humanity', 
      logoSvg: `<svg viewBox="0 0 120 40" class="w-full h-full"><rect width="120" height="40" fill="#005DAA" rx="4"/><path d="M 20 22 L 12 18 L 12 28 L 20 28 Z" fill="white"/><path d="M 20 22 L 28 18 L 28 28 L 20 28 Z" fill="#FF6B35"/><path d="M 12 18 L 20 12 L 28 18" stroke="white" stroke-width="1.5" fill="none"/><text x="73" y="17" text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="Arial">HABITAT FOR</text><text x="73" y="27" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="Arial">HUMANITY</text></svg>`
    }
  ];

  const offeringsList = [
    'Small Format Printing',
    'Large Format Graphics',
    'Awards & Recognition',
    'Promotional Products',
    'Shipping (Small Freight and LTL)',
    'Smart Hands Field Services',
    'Warehousing',
    'Network Cabling Installation'
  ];

  return (
    <div className="min-h-screen bg-background font-plex">
      <Toaster position="top-center" />
      
      {/* Navigation */}
      <header 
        className="fixed top-0 inset-x-0 z-50 bg-[rgba(252,251,247,0.9)] backdrop-blur-sm border-b border-border"
        data-testid="site-header"
      >
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a 
            href="#home" 
            className="flex items-center gap-3 transition-transform hover:scale-105 duration-150" 
            data-testid="site-logo-link"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('home');
            }}
          >
            <div className="w-12 h-12 bg-[#0A1B2E] rounded-xl p-2 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_db9d9eeb-eba2-43bb-aace-3ceec14de46a/artifacts/nn1cqftm_2TLogo.png" 
                alt="2TI Corp Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className="font-chivo font-bold text-[#0A1B2E] text-xl block leading-none">2TI CORP</span>
              <span className="text-[#C9A227] text-xs font-medium tracking-wider">BUSINESS SOLUTIONS</span>
            </div>
          </a>
          
          <nav className="hidden md:flex items-center gap-8">
            <a 
              href="#services" 
              className="text-sm font-medium text-foreground hover:text-accent transition-colors duration-150"
              data-testid="nav-services-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('services');
              }}
            >
              Services
            </a>
            <a 
              href="#products" 
              className="text-sm font-medium text-foreground hover:text-accent transition-colors duration-150"
              data-testid="nav-products-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('products');
              }}
            >
              Products
            </a>
            <a 
              href="#clients" 
              className="text-sm font-medium text-foreground hover:text-accent transition-colors duration-150"
              data-testid="nav-clients-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('clients');
              }}
            >
              Clients
            </a>
            <a 
              href="#about" 
              className="text-sm font-medium text-foreground hover:text-accent transition-colors duration-150"
              data-testid="nav-about-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('about');
              }}
            >
              About
            </a>
            <a 
              href="#contact" 
              className="text-sm font-medium text-foreground hover:text-accent transition-colors duration-150"
              data-testid="nav-contact-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
              }}
            >
              Contact
            </a>
            <Link
              to="/shipping"
              className="text-sm font-semibold text-[#C9A227] hover:text-[#B69121] transition-colors duration-150"
              data-testid="nav-shipping-link"
            >
              Request Shipment
            </Link>
          </nav>
          
          <div className="flex items-center gap-3">
            <Button 
              className="bg-primary text-primary-foreground hover:bg-[#173654] focus-visible:ring-2 focus-visible:ring-ring transition-all duration-150 hover:-translate-y-[1px]"
              data-testid="nav-cta-button"
              onClick={() => scrollToSection('contact')}
            >
              Contact Us
            </Button>
            <button
              className="md:hidden p-2 hover:bg-muted rounded-md transition-colors duration-150"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 gap-2">
              <a 
                href="#services" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
              >
                Services
              </a>
              <a 
                href="#products" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('products');
                }}
              >
                Products
              </a>
              <a 
                href="#clients" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('clients');
                }}
              >
                Clients
              </a>
              <a 
                href="#about" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('about');
                }}
              >
                About
              </a>
              <a 
                href="#contact" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('contact');
                }}
              >
                Contact
              </a>
              <Link
                to="/shipping"
                className="px-4 py-2 text-sm font-semibold text-[#C9A227] hover:bg-muted rounded-md transition-colors duration-150"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-shipping-link"
              >
                Request Shipment
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-16">
        {/* Hero Section */}
        <section 
          id="home"
          className="relative overflow-hidden bg-gradient-to-br from-[#0A1B2E] via-[#0F2742] to-[#0A1B2E] text-white"
          data-testid="hero-section"
        >
          <div 
            className="absolute inset-0 opacity-10" 
            aria-hidden="true" 
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1678794792867-565c9cd2fb55?auto=format&fit=crop&w=1600&q=60)', 
              backgroundSize: 'cover', 
              backgroundPosition: 'center' 
            }} 
          />
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40 relative">
            <div className="flex flex-col items-center text-center">
              <Reveal>
                <div className="mb-8">
                  <div className="relative inline-flex items-center justify-center w-40 h-40 mb-6">
                    {/* Outer glow ring */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#C9A227] via-[#B69121] to-[#C9A227] rounded-3xl blur-2xl opacity-40 animate-pulse"></div>
                    {/* Logo container with shadow */}
                    <div className="relative w-full h-full bg-[#0A1B2E] rounded-3xl shadow-2xl flex items-center justify-center p-6 border-2 border-[#C9A227]/30">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_db9d9eeb-eba2-43bb-aace-3ceec14de46a/artifacts/nn1cqftm_2TLogo.png" 
                        alt="2TI Corp Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  <h1 className="font-chivo text-2xl sm:text-3xl font-bold text-[#C9A227] tracking-wider">2TI CORP</h1>
                  <p className="text-[#E9D8A6] text-sm tracking-widest mt-1">BUSINESS SOLUTIONS</p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="text-[#C9A227] uppercase tracking-wider text-sm mb-4" data-testid="hero-subtitle">
                  Trusted by enterprises & government agencies
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <h2 
                  className="font-chivo text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-4xl mb-6" 
                  data-testid="hero-headline"
                >
                  Products, Services & Business Solutions
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p 
                  className="mt-4 max-w-2xl text-lg text-[#E9EAF0]" 
                  data-testid="hero-subhead"
                >
                  From corporate awards to shipping logistics, 2TI Corp delivers comprehensive solutions for Fortune 500 companies and government agencies.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="bg-accent text-accent-foreground hover:bg-[#B69121] focus-visible:ring-2 focus-visible:ring-ring transition-all duration-150 hover:-translate-y-[1px] shadow-lg"
                    data-testid="hero-primary-cta-button"
                    onClick={() => scrollToSection('services')}
                  >
                    Explore Services
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline" 
                    className="border-white/30 text-white hover:bg-white/10 transition-all duration-150"
                    data-testid="hero-secondary-cta-button"
                    onClick={() => scrollToSection('contact')}
                  >
                    Get a Quote
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Client Logos Marquee */}
        <section id="clients" className="py-12 sm:py-16 bg-white" data-testid="clients-section">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-sm uppercase tracking-wider text-accent font-semibold text-center mb-8">
                Trusted By Industry Leaders
              </p>
            </Reveal>
          </div>
          <div 
            className="overflow-hidden marquee-mask" 
            aria-label="Client logos carousel" 
            data-testid="client-logos-marquee"
          >
            <div className="marquee">
              <div className="marquee-track">
                {clients.map((client, index) => (
                  <div 
                    key={`client-${index}`}
                    className="inline-flex items-center justify-center px-6 py-3 bg-white rounded-lg min-w-[160px] h-20 border border-border shadow-sm hover:shadow-md transition-all duration-300 group"
                    aria-label={client.name}
                  >
                    <div 
                      className="w-[120px] h-10 flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: client.logoSvg }}
                    />
                  </div>
                ))}
              </div>
              <div className="marquee-track" aria-hidden="true">
                {clients.map((client, index) => (
                  <div 
                    key={`client-dup-${index}`}
                    className="inline-flex items-center justify-center px-6 py-3 bg-white rounded-lg min-w-[160px] h-20 border border-border shadow-sm"
                  >
                    <div 
                      className="w-[120px] h-10 flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: client.logoSvg }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section 
          id="services" 
          className="py-16 sm:py-20 lg:py-28 bg-background"
          data-testid="services-section"
        >
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <h2 className="font-chivo text-3xl sm:text-4xl font-semibold text-foreground mb-4">
                  Our Services
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Comprehensive logistics and technical services to support your business operations.
                </p>
              </div>
            </Reveal>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <Reveal key={index} delay={index * 0.1}>
                  <Card 
                    className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border"
                    data-testid={`services-card-${index + 1}`}
                  >
                    <CardHeader>
                      <div className="mb-4">{service.icon}</div>
                      <CardTitle className="font-chivo text-xl">{service.title}</CardTitle>
                      <CardDescription>{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.points.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-accent mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section 
          id="products" 
          className="py-16 sm:py-20 lg:py-28 bg-white"
          data-testid="products-section"
        >
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <h2 className="font-chivo text-3xl sm:text-4xl font-semibold text-foreground mb-4">
                  Our Products
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  High-quality products to elevate your brand and communicate your message effectively.
                </p>
              </div>
            </Reveal>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <Reveal key={index} delay={index * 0.1}>
                  <Card 
                    className="overflow-hidden h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border"
                    data-testid={`products-card-${index + 1}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                      <div className="absolute top-4 right-4 bg-white/90 p-3 rounded-full shadow-md">
                        {product.icon}
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="font-chivo text-xl mb-4">{product.title}</CardTitle>
                      <ul className="space-y-2">
                        {product.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-accent mt-1">✦</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardHeader>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section 
          id="about" 
          className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-[#F7F5EF] to-[#E7EEF6]"
          data-testid="about-section"
        >
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <Reveal>
                <div>
                  <Badge 
                    className="mb-6 bg-accent text-accent-foreground text-lg px-4 py-2"
                    data-testid="about-exp-badge"
                  >
                    40+ Years Combined Experience
                  </Badge>
                  <h2 className="font-chivo text-3xl sm:text-4xl font-semibold text-foreground mb-6">
                    About 2TI Corp
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      2TI Corp is your comprehensive business solutions partner, offering a unique combination of products and services under one roof. With over four decades of combined experience, our team has built lasting relationships with Fortune 500 companies, government agencies, and growing enterprises.
                    </p>
                    <p>
                      From corporate awards and promotional products to shipping logistics and network infrastructure, we understand that modern businesses need reliable partners who can deliver quality across multiple disciplines. That's exactly what we do.
                    </p>
                    <p className="font-semibold text-foreground">
                      Our clients include:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span>Fortune 500 companies seeking premium products and dependable services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span>Government agencies requiring compliance and reliability</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span>Growing enterprises looking for scalable solutions</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="relative">
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1659654932578-4a3e07f737db?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                      alt="2TI Corp team and services"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-8 rounded-xl shadow-xl">
                    <p className="font-chivo text-4xl font-bold">40+</p>
                    <p className="text-sm">Years Experience</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section 
          id="contact" 
          className="py-16 sm:py-20 lg:py-28 bg-white"
          data-testid="contact-section"
        >
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <h2 className="font-chivo text-3xl sm:text-4xl font-semibold text-foreground mb-4">
                  Get In Touch
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Ready to discuss your project? Contact us today and let's explore how we can help your business succeed.
                </p>
              </div>
            </Reveal>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <Reveal delay={0.1}>
                <div className="space-y-8">
                  <div>
                    <h3 className="font-chivo text-xl font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-1">Address</p>
                          <p className="text-muted-foreground leading-relaxed" data-testid="contact-address">
                            1489 East Warner Ave<br />
                            Santa Ana, CA 92705
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Email</p>
                          <a 
                            href="mailto:Info@2ticorp.com" 
                            className="text-accent hover:underline"
                            data-testid="contact-email"
                          >
                            Info@2ticorp.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
              
              <Reveal delay={0.2}>
                <Card data-testid="contact-form">
                  <CardHeader>
                    <CardTitle className="font-chivo">Send Us a Message</CardTitle>
                    <CardDescription>
                      Fill out the form below and we'll get back to you as soon as possible.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input 
                            id="firstName"
                            value={formData.firstName}
                            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                            required
                            data-testid="contact-firstname-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input 
                            id="lastName"
                            value={formData.lastName}
                            onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                            required
                            data-testid="contact-lastname-input"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input 
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input 
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          data-testid="contact-phone-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input 
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                          data-testid="contact-company-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="interestedIn">Interested In</Label>
                        <Select 
                          value={formData.interestedIn} 
                          onValueChange={(value) => setFormData({...formData, interestedIn: value})}
                        >
                          <SelectTrigger data-testid="contact-interested-select">
                            <SelectValue placeholder="Select a service or product" />
                          </SelectTrigger>
                          <SelectContent>
                            {offeringsList.map((offering, index) => (
                              <SelectItem key={index} value={offering}>
                                {offering}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea 
                          id="message"
                          rows={4}
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          required
                          data-testid="contact-message-textarea"
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-primary text-primary-foreground hover:bg-[#173654] transition-all duration-150"
                        data-testid="contact-form-submit-button"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12" data-testid="site-footer">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#0A1B2E] rounded-xl p-2 flex items-center justify-center border border-primary-foreground/20">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_db9d9eeb-eba2-43bb-aace-3ceec14de46a/artifacts/nn1cqftm_2TLogo.png" 
                    alt="2TI Corp Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <span className="font-chivo font-bold text-lg block leading-none">2TI CORP</span>
                  <span className="text-accent text-xs font-medium tracking-wider">BUSINESS SOLUTIONS</span>
                </div>
              </div>
              <p className="text-sm text-primary-foreground/80">
                Products, Services & Business Solutions
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <div className="space-y-2 text-sm text-primary-foreground/80">
                <p>1489 East Warner Ave</p>
                <p>Santa Ana, CA 92705</p>
                <p>Info@2ticorp.com</p>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <nav className="space-y-2 text-sm">
                <a href="#services" className="block text-primary-foreground/80 hover:text-accent transition-colors duration-150">Services</a>
                <a href="#products" className="block text-primary-foreground/80 hover:text-accent transition-colors duration-150">Products</a>
                <a href="#about" className="block text-primary-foreground/80 hover:text-accent transition-colors duration-150">About</a>
                <a href="#contact" className="block text-primary-foreground/80 hover:text-accent transition-colors duration-150">Contact</a>
                <Link to="/shipping" className="block text-accent hover:text-[#B69121] transition-colors duration-150 font-medium">Request Shipment</Link>
                <Link to="/admin/login" className="block text-primary-foreground/40 hover:text-primary-foreground/70 transition-colors duration-150 text-xs mt-3">Admin Portal</Link>
              </nav>
            </div>
          </div>
          
          <Separator className="my-8 bg-primary-foreground/20" />
          
          <div className="text-center text-sm text-primary-foreground/80">
            <p>© 2026 2TI Corp. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/shipping" element={<ShippingPage />} />
      <Route path="/admin/login" element={<AdminProviderWrap><AdminLoginPage /></AdminProviderWrap>} />
      <Route path="/admin" element={<AdminProviderWrap><AdminGuard /></AdminProviderWrap>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="clients" element={<AdminClientsPage />} />
        <Route path="vendors" element={<AdminVendorsPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="activity-log" element={<AdminActivityLogPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
      </Route>
    </Routes>
  );
}

/* Admin wrapper components - lazy imports to keep public pages fast */
import { AdminProvider, useAdmin } from './admin/AdminContext';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminClients from './admin/AdminClients';
import AdminVendors from './admin/AdminVendors';
import AdminUsersComp from './admin/AdminUsers';
import AdminActivityLog from './admin/AdminActivityLog';
import AdminReports from './admin/AdminReports';
import { Toaster as SonnerToaster } from './components/ui/sonner';

function AdminProviderWrap({ children }) {
  return <AdminProvider><SonnerToaster richColors position="top-right" />{children}</AdminProvider>;
}

function AdminGuard() {
  const { user, loading } = useAdmin();
  const navigate = React.useCallback(() => {}, []);
  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-400">Loading...</p></div>;
  if (!user) { window.location.href = '/admin/login'; return null; }
  return <AdminLayout />;
}

function AdminLoginPage() { return <AdminLogin />; }
function AdminDashboardPage() { return <AdminDashboard />; }
function AdminClientsPage() { return <AdminClients />; }
function AdminVendorsPage() { return <AdminVendors />; }
function AdminUsersPage() { return <AdminUsersComp />; }
function AdminActivityLogPage() { return <AdminActivityLog />; }
function AdminReportsPage() { return <AdminReports />; }

export default App;